// Home page Total Vehicles tile. The tile counts every operational status, so
// it reads stats.totalVehicles (built by the backend without the page's
// operationalStatus filter) instead of the operational/non_operational keys
// that the rest of the page uses.

import { INDICATOR_PRESETS } from "../../Static/Config/indicatorPresets";

// Non-Operational and Onboarding reuse the Inventory status marker (icon, colour,
// background) so the two screens look the same. Inventory shows no marker for
// Operational, so its badge is defined here in the same palette.
const presetFor = (valueCode) => {
  const rule = INDICATOR_PRESETS.operationalStatus.rules.find((r) => r.match.includes(valueCode));
  return { icon: rule.icon, color: rule.color, background: rule.background };
};

export const TOTAL_VEHICLE_STATUSES = [
  {
    code: "operational",
    label: "Operational",
    icon: "status-operational",
    color: "#027A48",
    background: "#E7F6EC",
    diffKey: "operational_diff",
    positiveWhenIncrease: true,
    getDefinition: (entityPlural) =>
      `${entityPlural} tagged as Operational`,
  },
  {
    code: "non_operational",
    label: "Non-Operational",
    ...presetFor("non_operational"),
    diffKey: "non_operational_diff",
    positiveWhenIncrease: false,
    getDefinition: (entityPlural) =>
      `${entityPlural} that are out of service due to maintenance, repairs, or technical issues`,
  },
  {
    code: "onboarding_inprogress",
    label: "Onboarding In Progress",
    ...presetFor("onboarding_inprogress"),
    // More vehicles onboarding means the fleet is growing.
    positiveWhenIncrease: true,
    getDefinition: (entityPlural) =>
      `${entityPlural} still being onboarded`,
  },
];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const toCount = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

// Percentage change of todayCount against the count before `change` was applied.
// Moved unchanged from TotalVehiclesCard so the day-over-day numbers stay the same.
export const calculateChangePercentage = (change, todayCount) => {
  if (change === 0) return 0;

  const previousCount = todayCount - change;
  if (previousCount === 0) {
    return Math.abs(todayCount) * 100;
  }

  return (Math.abs(change) / Math.abs(previousCount)) * 100;
};

export const formatCount = (value) =>
  typeof value === "number" && Number.isFinite(value) ? value.toLocaleString("en-US") : "-";

// One decimal, trailing ".0" dropped: 1.7, 0.9, 2.
export const formatShare = (value) => {
  if (!Number.isFinite(value)) return "0";
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? rounded.toString() : rounded.toFixed(1);
};

// "2026-10-31" -> "31 Oct'26"; anything else is returned unchanged.
export const formatSnapshotDate = (isoDate) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate || "");
  if (!match) return isoDate || "";
  const [, year, month, day] = match;
  return `${day} ${MONTHS[parseInt(month, 10) - 1]}'${year.slice(2)}`;
};

export const getTotalVehiclesTileData = (stats = {}) => {
  const tile = stats?.totalVehicles;
  if (!tile) return null;

  const statusCounts = tile.statusCounts || {};
  const statusDisplayNames = tile.statusDisplayNames || {};
  const total = toCount(tile.total);
  const lastMonthTotal = toCount(tile.lastMonthTotal);
  // Nothing added before this month (a new org): a
  // change against 0 would read as the whole fleet being added this month.
  const hasLastMonth = lastMonthTotal > 0;
  const monthChange = total - lastMonthTotal;

  const rows = TOTAL_VEHICLE_STATUSES
    .filter((status) => status.code in statusCounts)
    .filter((status) => status.code !== "onboarding_inprogress" || tile.hasOnboardingStatus)
    .map((status) => {
      const count = toCount(statusCounts[status.code]);
      const row = {
        ...status,
        count,
        share: total > 0 ? (count / total) * 100 : 0,
        filterValue: statusDisplayNames[status.code] || status.label,
      };
      // statusDiffs covers all three statuses; the page-level *_diff keys are the
      // fallback for a backend that does not send it yet (they have no onboarding).
      const rawDayChange = tile.statusDiffs?.[status.code] ?? stats[status.diffKey];
      if (rawDayChange !== undefined && rawDayChange !== null) {
        const dayChange = toCount(rawDayChange);
        row.dayChange = dayChange;
        row.dayChangePercentage = calculateChangePercentage(dayChange, count);
      }
      return row;
    });

  return {
    total,
    lastMonthTotal,
    lastMonthEndDate: tile.lastMonthEndDate,
    monthChange,
    monthChangePercentage: hasLastMonth ? (Math.abs(monthChange) / lastMonthTotal) * 100 : null,
    hasLastMonth,
    hasOnboardingStatus: Boolean(tile.hasOnboardingStatus),
    rows,
  };
};

export const getTotalVehiclesTooltip = (entityPlural, hasOnboardingStatus) =>
  hasOnboardingStatus
    ? `Includes Operational, Non-Operational and Onboarding In Progress ${entityPlural}. Every other number on this page excludes Onboarding In Progress.`
    : `Includes Operational and Non-Operational ${entityPlural}.`;

export const getLiveStatusScopeNote = (entityPlural, hasOnboardingStatus) =>
  hasOnboardingStatus
    ? `Counts cover Operational and Non-Operational ${entityPlural} only. Onboarding In Progress ${entityPlural} are excluded.`
    : `Counts cover Operational and Non-Operational ${entityPlural}.`;

// Lines for the month-change tooltip. Last month is the same entities that were
// added (configBOP.addedOn) before this month, so the change is what was added since.
export const getMonthChangeTooltipLines = (data) => {
  if (!data) return [];
  const snapshotDate = formatSnapshotDate(data.lastMonthEndDate) || "end of last month";
  return [
    `On platform as of ${snapshotDate}: ${formatCount(data.lastMonthTotal)}`,
    `Onboarded this month: ${formatCount(Math.max(data.monthChange, 0))}`,
  ];
};
